// src/components/Common/HolidayCalendar.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload as ImportIcon,
  CalendarMonth as CalendarIcon,
  EventBusy as HolidayIcon,
  Schedule as TimeIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import api from '../../services/api';

interface Holiday {
  id?: number;
  holiday_date: string;
  name: string;
  is_enabled: boolean;
  all_day_closed: boolean;
  special_hours?: {
    openTime: string;
    closeTime: string;
  };
  action_pbxapp?: number;
  action_pbxdigits?: string;
  action_pbxdesc?: string;
  is_recurring: boolean;
  source: 'manual' | 'api' | 'template';
}

interface AvailableHoliday {
  date: string;
  name: string;
  localName: string;
  countryCode: string;
  fixed: boolean;
  global: boolean;
  types: string[];
  isAlreadyAdded: boolean;
}

interface HolidayCalendarProps {
  entityType: 'extension' | 'phone_number' | 'company';
  entityId?: string;
  customerID: number;
  year?: number;
}

const HolidayCalendar: React.FC<HolidayCalendarProps> = ({
  entityType,
  entityId,
  customerID,
  year = new Date().getFullYear(),
}) => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [availableHolidays, setAvailableHolidays] = useState<AvailableHoliday[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState('CA');
  
  // Dialog states
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [availableHolidaysDialogOpen, setAvailableHolidaysDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // Form data
  const [holidayForm, setHolidayForm] = useState<Partial<Holiday>>({
    holiday_date: '',
    name: '',
    is_enabled: true,
    all_day_closed: true,
    is_recurring: false,
    action_pbxapp: 7, // Default to voicemail
  });
  

  // Temporary state for old API fallback
  const [overflowActions, setOverflowActions] = useState<any[]>([]);
  const [overflowTargets, setOverflowTargets] = useState<any[]>([]);

  // Fetch actions using old API for now
  useEffect(() => {
    const fetchActions = async () => {
      try {
        const response = await api.get('/endpoints/ring_groups_v2/overflow-actions');
        if (response.data.success) {
          setOverflowActions(response.data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch actions:', err);
      }
    };
    fetchActions();
  }, []);

  // Fetch targets using old API for now
  useEffect(() => {
    if (!holidayDialogOpen || !holidayForm.action_pbxapp) {
      setOverflowTargets([]);
      return;
    }

    const fetchTargets = async () => {
      const requiresTargetActions = [1, 3, 4, 7, 11, 16, 17, 19, 20];
      if (!holidayForm.action_pbxapp || !requiresTargetActions.includes(holidayForm.action_pbxapp)) {
        setOverflowTargets([]);
        return;
      }

      try {
        const params = customerID ? { customerId: customerID } : {};
        const response = await api.get(`/endpoints/ring_groups_v2/overflow-targets/${holidayForm.action_pbxapp}`, { params });
        if (response.data.success) {
          const targets = response.data.data || [];
          setOverflowTargets(targets);
          
          // Set default for voicemail when adding new holiday for extension
          if (!editingHoliday && holidayForm.action_pbxapp === 7 && entityType === 'extension' && entityId && !holidayForm.action_pbxdigits) {
            const extensionExists = targets.some((t: any) => t.value === entityId);
            if (extensionExists) {
              setHolidayForm(prev => ({ ...prev, action_pbxdigits: entityId }));
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch targets:', err);
      }
    };
    
    fetchTargets();
  }, [holidayDialogOpen, holidayForm.action_pbxapp, entityType, entityId, editingHoliday]);


  useEffect(() => {
    fetchHolidays();
  }, [year, entityId]);

  useEffect(() => {
    fetchAvailableHolidays();
  }, [year, entityId, selectedCountry]);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const params = { 
        year,
        ...(customerID && { customerId: customerID })
      };
      
      const endpoint = entityId 
        ? `/endpoints/holidays/${entityType}/${entityId}`
        : '/endpoints/holidays';
      
      const response = await api.get(endpoint, { params });
      if (response.data.success) {
        setHolidays(response.data.data || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableHolidays = async () => {
    try {
      let url = `/endpoints/holidays/available?year=${year}&country=${selectedCountry}`;
      if (entityId) {
        url += `&entity_id=${entityId}&entity_type=${entityType}`;
      }
      const response = await api.get(url);
      if (response.data.success) {
        setAvailableHolidays(response.data.data.holidays || []);
      }
    } catch (err) {
      console.error('Failed to fetch available holidays:', err);
    }
  };


  const handleAddHoliday = () => {
    setEditingHoliday(null);
    setHolidayForm({
      holiday_date: '',
      name: '',
      is_enabled: true,
      all_day_closed: true,
      is_recurring: false,
      action_pbxapp: 7, // Default to voicemail
      action_pbxdigits: entityType === 'extension' ? entityId : '', // Default to current extension if editing an extension
    });
    setHolidayDialogOpen(true);
  };

  const handleEditHoliday = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    // Ensure all fields are properly set
    const formData = {
      holiday_date: holiday.holiday_date,
      name: holiday.name,
      is_enabled: holiday.is_enabled,
      all_day_closed: holiday.all_day_closed,
      is_recurring: holiday.is_recurring,
      action_pbxapp: holiday.action_pbxapp,
      action_pbxdigits: holiday.action_pbxdigits,
      action_pbxdesc: holiday.action_pbxdesc,
      special_hours: holiday.special_hours || { openTime: '09:00', closeTime: '17:00' },
    };
    console.log('Setting form data for edit:', formData);
    setHolidayForm(formData);
    setHolidayDialogOpen(true);
  };

  const handleSaveHoliday = async () => {
    console.log('Save button clicked');
    console.log('Holiday form data:', holidayForm);
    console.log('Editing holiday:', editingHoliday);
    
    try {
      const data = {
        ...holidayForm,
        entity_id: entityId,
        entity_type: entityType,
      };

      if (editingHoliday) {
        console.log('Updating holiday with ID:', editingHoliday.id);
        await api.put(`/endpoints/holidays/${editingHoliday.id}`, data);
        setSuccess('Holiday updated successfully');
      } else {
        await api.post('/endpoints/holidays', data);
        setSuccess('Holiday created successfully');
      }
      
      setHolidayDialogOpen(false);
      fetchHolidays();
    } catch (err: any) {
      console.error('Save holiday error:', err);
      setError(err.response?.data?.message || 'Failed to save holiday');
    }
  };

  const handleDeleteHoliday = async () => {
    if (!selectedHoliday) return;

    try {
      await api.delete(`/endpoints/holidays/${selectedHoliday.id}`);
      setSuccess('Holiday deleted successfully');
      setDeleteDialogOpen(false);
      fetchHolidays();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete holiday');
    }
  };

  const handleAddAvailableHoliday = async (holiday: AvailableHoliday) => {
    try {
      const data = {
        holiday_date: holiday.date,
        name: holiday.name,
        is_enabled: true,
        all_day_closed: true,
        is_recurring: holiday.fixed,
        action_pbxapp: 7, // Default to voicemail
        action_pbxdigits: entityType === 'extension' ? entityId : '', // Default to current extension if editing an extension
        entity_id: entityId,
        entity_type: entityType,
        source: 'api'
      };
      
      await api.post('/endpoints/holidays', data);
      setSuccess(`Added ${holiday.name}`);
      fetchHolidays();
      fetchAvailableHolidays(); // Refresh to update isAlreadyAdded status
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add holiday');
    }
  };

  const getMonthHolidays = (month: number) => {
    return holidays.filter(holiday => {
      const holidayMonth = new Date(holiday.holiday_date).getMonth();
      return holidayMonth === month;
    });
  };

  const formatHolidayDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM d');
    } catch {
      return dateString;
    }
  };

  const getActionName = (actionId: number) => {
    const action = overflowActions.find(a => a.action_key === actionId);
    return action?.action_name || 'Unknown';
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CalendarIcon color="primary" />
            <Typography variant="h6">
              Holiday Calendar {year}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={() => setAvailableHolidaysDialogOpen(true)}
              size="small"
            >
              Add from List
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddHoliday}
              size="small"
            >
              Add Holiday
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : holidays.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <HolidayIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No holidays configured
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add holidays manually or import from a template
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={() => setAvailableHolidaysDialogOpen(true)}
            >
              Add from Holiday List
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {Array.from({ length: 12 }, (_, i) => i).map(month => {
              const monthHolidays = getMonthHolidays(month);
              if (monthHolidays.length === 0) return null;

              return (
                <Grid item xs={12} sm={6} md={4} key={month}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {format(new Date(year, month), 'MMMM')}
                      </Typography>
                      <List dense>
                        {monthHolidays.map(holiday => (
                          <ListItem
                            key={holiday.id}
                            sx={{
                              opacity: holiday.is_enabled ? 1 : 0.5,
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2">
                                    {formatHolidayDate(holiday.holiday_date)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {holiday.name}
                                  </Typography>
                                  {holiday.is_recurring && (
                                    <Chip label="Recurring" size="small" variant="outlined" />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  {holiday.all_day_closed ? (
                                    <Typography variant="caption" color="error">
                                      Closed all day
                                    </Typography>
                                  ) : (
                                    <Typography variant="caption">
                                      Special hours: {holiday.special_hours?.openTime} - {holiday.special_hours?.closeTime}
                                    </Typography>
                                  )}
                                  {holiday.action_pbxapp && (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                      → {getActionName(holiday.action_pbxapp)}
                                      {holiday.action_pbxdigits && (
                                        <> → {holiday.action_pbxdigits}</>
                                      )}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleEditHoliday(holiday)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                edge="end"
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedHoliday(holiday);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>

      {/* Holiday Dialog */}
      <Dialog open={holidayDialogOpen} onClose={() => setHolidayDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingHoliday ? 'Edit Holiday' : 'Add Holiday'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Holiday Date"
              type="date"
              fullWidth
              value={holidayForm.holiday_date || ''}
              onChange={(e) => setHolidayForm({ ...holidayForm, holiday_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={!!editingHoliday}
            />

            <TextField
              label="Holiday Name"
              fullWidth
              value={holidayForm.name || ''}
              onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={holidayForm.is_enabled !== false}
                  onChange={(e) => setHolidayForm({ ...holidayForm, is_enabled: e.target.checked })}
                />
              }
              label="Enabled"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={holidayForm.all_day_closed !== false}
                  onChange={(e) => setHolidayForm({ ...holidayForm, all_day_closed: e.target.checked })}
                />
              }
              label="Closed All Day"
            />

            {!holidayForm.all_day_closed && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Open Time"
                  type="time"
                  fullWidth
                  value={holidayForm.special_hours?.openTime || '09:00'}
                  onChange={(e) => setHolidayForm({
                    ...holidayForm,
                    special_hours: {
                      ...holidayForm.special_hours,
                      openTime: e.target.value,
                      closeTime: holidayForm.special_hours?.closeTime || '17:00',
                    }
                  })}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Close Time"
                  type="time"
                  fullWidth
                  value={holidayForm.special_hours?.closeTime || '17:00'}
                  onChange={(e) => setHolidayForm({
                    ...holidayForm,
                    special_hours: {
                      ...holidayForm.special_hours,
                      openTime: holidayForm.special_hours?.openTime || '09:00',
                      closeTime: e.target.value,
                    }
                  })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}

            <Divider />

            <Typography variant="subtitle2">Holiday Action</Typography>

            <FormControl fullWidth>
              <InputLabel>Action</InputLabel>
              <Select
                value={holidayForm.action_pbxapp || 7}
                onChange={(e) => {
                  const actionId = Number(e.target.value);
                  setHolidayForm({ ...holidayForm, action_pbxapp: actionId, action_pbxdigits: '' });
                }}
                label="Action"
              >
                {overflowActions.map((action) => (
                  <MenuItem key={action.action_key} value={action.action_key}>
                    {action.action_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {overflowTargets.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Target</InputLabel>
                <Select
                  value={holidayForm.action_pbxdigits || ''}
                  onChange={(e) => setHolidayForm({ ...holidayForm, action_pbxdigits: e.target.value })}
                  label="Target"
                >
                  <MenuItem value="">Select target</MenuItem>
                  {overflowTargets.map((target) => (
                    <MenuItem key={target.value} value={target.value}>
                      {target.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={holidayForm.is_recurring || false}
                  onChange={(e) => setHolidayForm({ ...holidayForm, is_recurring: e.target.checked })}
                />
              }
              label="Recurring Annually"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHolidayDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveHoliday}
            variant="contained"
            disabled={!holidayForm.name || (!editingHoliday && !holidayForm.holiday_date)}
          >
            {editingHoliday ? 'Update' : 'Add'} Holiday
          </Button>
        </DialogActions>
      </Dialog>

      {/* Available Holidays Dialog */}
      <Dialog open={availableHolidaysDialogOpen} onClose={() => setAvailableHolidaysDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Add Holidays from List</Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
              >
                <MenuItem value="CA">Canada</MenuItem>
                <MenuItem value="US">United States</MenuItem>
                <MenuItem value="MX">Mexico</MenuItem>
                <MenuItem value="GB">United Kingdom</MenuItem>
                <MenuItem value="AU">Australia</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {availableHolidays.map((holiday) => (
              <React.Fragment key={holiday.date}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{holiday.name}</Typography>
                        {holiday.fixed && <Chip label="Annual" size="small" variant="outlined" />}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption">
                          {format(parseISO(holiday.date), 'MMMM d, yyyy')}
                        </Typography>
                        {holiday.types.length > 0 && (
                          <Typography variant="caption" sx={{ ml: 2 }}>
                            Type: {holiday.types.join(', ')}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleAddAvailableHoliday(holiday)}
                      disabled={holiday.isAlreadyAdded}
                    >
                      {holiday.isAlreadyAdded ? 'Added' : 'Add'}
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvailableHolidaysDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the holiday "{selectedHoliday?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteHoliday} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HolidayCalendar;